import { Button, Form, Modal, Steps, Typography } from 'antd'
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input'
import Countries from '../../../utils/countries'
import { useCustomFieldsContext } from '../../../context/customFields'
import { FieldTargetType } from '../../../context/types'
import useOnboardingSteps from '../../../hooks/useOnboardingSteps'
import { useAuthContext } from '../../../context/auth'
import { customSessionStorage } from '../../../context/customStorage'
import { transformCustomFields, TValuesMap, updateFieldWithSelectAll } from '../../../utils/custom-fields'
import { DEFAULT_COUNTRY } from '../../../constants'
import { useIntegrationContext } from '../../../context/integration'

import './Onboarding.less'

const { Step } = Steps
const { Title } = Typography

const Onboarding: FunctionComponent = () => {
	const history = useHistory()
	const { auth } = useAuthContext()
	const { customFields, actions } = useCustomFieldsContext()
	const { integration } = useIntegrationContext()
	const [form] = Form.useForm()
	const [currentStep, setCurrentStep] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [errors, setErrors] = useState<string[]>([])
	const [showError, setShowError] = useState<boolean>(false)
	const [allValues, setAllValues] = useState<{ [key: string]: any }>({})

	const steps = useOnboardingSteps()

	useEffect(() => {
		if (auth.user && !customFields[FieldTargetType.onboarding].loaded) {
			actions.getOnboardingCustomFields(
				[FieldTargetType.onboarding, ...(auth.user?.isExpert ? [FieldTargetType.experts] : [FieldTargetType.members])],
				integration.referrer
			)
		}
	}, [auth.user])

	useEffect(() => {
		setAllValues({})
		if (errors[currentStep]) {
			setShowError(true)
		} else {
			setShowError(false)
		}
	}, [currentStep])

	const initialValues = useMemo<{ [key: string]: any }>(
		() => ({
			eeo_is_disabled: auth.user?.eeo_is_disabled,
			eeo_is_lgbtq: auth.user?.eeo_is_lgbtq,
			eeo_is_veteran: auth.user?.eeo_is_veteran,
			ethnicity: auth.user?.ethnicity,
			ageRange: auth.user?.ageRange,
			gender: auth.user?.gender,
			socialLinks: auth.user?.socialLinks || [],
			countryId: auth.user?.country?.id,
			stateId: auth.user?.state?.id,
			languages: auth.user?.languages?.map(({ iso }) => iso),
			city: auth.user?.city,
			phone_country_code:
				auth.user?.phone_country_code ||
				parsePhoneNumber(`+${auth.user?.phone_country_id}${auth.user?.phone}`)?.country ||
				DEFAULT_COUNTRY.iso,
			phone_country_id: Countries.options.some(
				({ country }) => getCountryCallingCode(country) === auth.user?.phone_country_id
			)
				? auth.user?.phone_country_id
				: '1',
			phone: auth.user?.phone,
			...transformCustomFields(customFields.allFields.fields),
		}),
		[auth.user, customFields.allFields]
	)

	useEffect(() => {
		form.setFieldsValue({
			...transformCustomFields(customFields.allFields.fields),
		})
	}, [customFields.allFields])

	const goToHome = useCallback(() => {
		const redirect = customSessionStorage.getItem('redirectUrl')

		if (redirect) {
			history.replace(redirect)
		} else {
			history.replace(auth.user?.isExpert ? '/profile' : '/experts')
		}
		customSessionStorage.removeItem('redirectUrl')
	}, [history, auth.user])

	const onFinish = () => {
		if (errors[currentStep]) {
			setShowError(true)
		} else {
			setLoading(true)
			steps[currentStep]
				?.onSubmit(form.getFieldsValue())
				.then(() => {
					skip()
					setLoading(false)
				})
				.catch((error) => {
					updateError(error)
					setShowError(true)
					setLoading(false)
				})
		}
	}

	const onFieldsChange = (fields: any[]) => {
		if (fields.length) {
			updateError('')
		}
	}

	const onValuesChange = (values: TValuesMap, updatedValues: TValuesMap) => {
		if (steps[currentStep].customFields) {
			let newAllValues = { ...updatedValues }

			if (Object.values(values).length) {
				const field = customFields[FieldTargetType.onboarding].fields.find(
					({ name }) => name === Object.keys(values)[0]
				)

				if (field.context?.select_all) {
					newAllValues = updateFieldWithSelectAll({
						newAllValues,
						values,
						updatedValues,
						allValues,
						form,
						field,
					})
				}
			}
			setAllValues(newAllValues)
		}
	}

	const back = () => {
		setCurrentStep(currentStep - 1)
	}
	const skip = () => {
		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1)
		} else {
			goToHome()
		}
	}

	const Footer = () => {
		return (
			<div className="onboarding-footer">
				{!!currentStep && (
					<Button className="back-btn" type="ghost" shape="round" onClick={back}>
						<i className="back-arrow">&#8592; </i> Back
					</Button>
				)}
				<div className="right-buttons">
					{steps[currentStep]?.skippable && (
						<Button className="skip-btn" type="ghost" shape="round" onClick={skip}>
							Skip
						</Button>
					)}
					<Button
						className="next-btn"
						type="primary"
						shape="round"
						htmlType="submit"
						form="OnboardingForm"
						loading={loading}
						ghost={steps[currentStep]?.isIntegrationStep}
					>
						{currentStep === steps.length - 1 ? (
							auth.user.isExpert ? (
								'Customize Profile'
							) : (
								'Find Your Expert'
							)
						) : (
							<>
								Next<span className="next-step">: {steps[currentStep + 1]?.title}</span>
							</>
						)}
					</Button>
				</div>
			</div>
		)
	}

	const ModalTitle = () => {
		const stepTitle = (title: string, className?: string) => (
			<span className={`step-title ${className ?? ''}`}>{title}</span>
		)
		const customModalTitle = steps[currentStep]?.modalTitle === null
		return (
			<div className="onboarding-header">
				<div className={`RequestCallModalSteps ${customModalTitle && 'without-modal-title'}`}>
					<Steps current={currentStep} labelPlacement="vertical" responsive>
						{steps.map((step) => (
							<Step title={stepTitle(step.title, step.stepTitleClass)} key={step.title} />
						))}
					</Steps>
				</div>
				{!customModalTitle && (
					<Title className="title" level={3}>
						<span dangerouslySetInnerHTML={{ __html: steps[currentStep]?.modalTitle || steps[currentStep]?.title }} />
					</Title>
				)}
			</div>
		)
	}

	function updateError(error: string): void {
		const newErrors = [...errors]
		newErrors[currentStep] = error
		setErrors(newErrors)
	}

	return (
		<div className="onboarding-page">
			<Modal
				className="onboarding"
				title={<ModalTitle />}
				visible
				confirmLoading={customFields.loading}
				onCancel={goToHome}
				footer={<Footer />}
				maskStyle={{ zIndex: 998 }}
			>
				<Form
					name="OnboardingForm"
					form={form}
					onFinish={onFinish}
					scrollToFirstError
					layout="vertical"
					onFieldsChange={onFieldsChange}
					onValuesChange={onValuesChange}
					initialValues={initialValues}
				>
					{steps[currentStep]?.component({ form, updateError })}
					{showError && errors[currentStep] && <span className="error">* {errors[currentStep]}</span>}
				</Form>
			</Modal>
		</div>
	)
}

export default Onboarding
